import React, { useState, useEffect } from "react";
import "./MobileChatMessages.scss";
import { FiEdit2, FiCheck, FiX } from "react-icons/fi";

const MobileChatMessages = ({
  properties,
  mobileMessageText,
  setMobileMessageText,
  mobileRecipients,
  setMobileRecipients,
  handleMobileBroadcast,
  setMessageTypePassed,
  isLoading,
  isError,
  errorMessage,
  selectedProperty,
  setSelectedProperty,
}) => {
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [messageType, setMessageType] = useState("custom");
  useEffect(() => {
    // Set the message type when the component mounts
    setMessageTypePassed(messageType);
  }, [messageType]);
  const [messageStatus, setMessageStatus] = useState(null);
  const [selectedPropertyData, setSelectedPropertyData] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editedTemplateText, setEditedTemplateText] = useState("");

  // Templates for common messages
  const [messageTemplates, setMessageTemplates] = useState([
    {
      type: "rent_reminder",
      text: "Hello [Tenant Name],\nRent: [Rent Amount]\nWater Bill: [Water Amount]\nGarbage: [Garbage Amount]\nUnpaid Deposit: [Deposit Amount] (if applicable)\nLate Fee: [Late Fee Amount] (if applicable)\nArrears: [Arrears Amount]\nTotal: [Total Amount].\nNote that these bills should be paid by the 5th of this month.\nBy Management,\nThank you.",
      editable: false,
    },
    {
      type: "late_rent_reminder",
      text: "Hello [Tenant Name],\nRent: [Rent Amount]\nWater Bill: [Water Amount]\nGarbage: [Garbage Amount]\nUnpaid Deposit: [Deposit Amount] (if applicable)\nLate Fee: [Late Fee Amount] (if applicable)\nArrears: [Arrears Amount]\nTotal: [Total Amount].\nNote that the deadline for this invoice has already passed.\nBy Management,\nThank you.",
      editable: false,
    },
    {
      type: "maintenance",
      text: "Dear tenant, We hope this message finds you well. We wanted to inform you that there will be a temporary interruption of water supply/electricity supply from [DATE] to [DATE]. We apologize for any inconveniences. Thank you.",
      editable: true,
    },
    {
      type: "security",
      text: "Dear tenant, Your safety is our top priority. Ensure you always leave the gate closed when coming in & out of the building. We would also like to remind all tenants to maintain vigilance and report any suspicious activities in the building. Thank you.",
      editable: true,
    },
    {
      type: "payment_info",
      text: "Dear tenant, Note that rent payments are done to the given account no. [Bank name/Paybill/Till][Store number/Account no], cash payments will not be accepted and it will be at your own risk. Thank you.",
      editable: true,
    },
    {
      type: "garbage",
      text: "Dear tenant, This is to inform you that garbage collection will be on every Thursday at 8.00 am. Please ensure you have removed your garbage on time.",
      editable: true,
    },
  ]);

  // Check if current message type requires automatic targeting
  const isAutoTargetedMessage =
    messageType === "rent_reminder" || messageType === "late_rent_reminder";

  // Handle property selection
  const handlePropertyChange = (e) => {
    const propertyId = e.target.value;
    setSelectedProperty(propertyId);
    setSelectedTenants([]);

    // Find the selected property
    const property = properties.find((p) => p._id === propertyId);

    // Extract tenants from the houses array
    if (property) {
      const tenants = property.houses
        .filter((house) => house.tenant)
        .map((house) => ({
          _id: house.tenant?.user?._id,
          name: house.tenant.user?.name || "Unnamed Tenant",
          phone_number: house.tenant.user?.phone || "No phone",
          house_no: house.house_no,
        }));

      setSelectedPropertyData({
        ...property,
        tenants: tenants,
      });
    }
  };

  // Handle tenant selection
  const handleTenantSelection = (tenantId) => {
    if (selectedTenants.includes(tenantId)) {
      setSelectedTenants(selectedTenants.filter((id) => id !== tenantId));
    } else {
      setSelectedTenants([...selectedTenants, tenantId]);
    }

    // Update the mobile recipients
    updateRecipients([...selectedTenants, tenantId]);
  };

  // Select all tenants in the property
  const handleSelectAllTenants = (property) => {
    if (!property || !property.tenants) return;

    const tenantIds = property.tenants.map((tenant) => tenant._id);
    setSelectedTenants(tenantIds);
    // Update the mobile recipients
    updateRecipients(tenantIds);
  };

  // Update recipients for broadcasting
  const updateRecipients = (tenantIds) => {
    if (!selectedPropertyData || !selectedPropertyData.tenants) return;

    const recipients = selectedPropertyData.tenants
      .filter((tenant) => tenantIds.includes(tenant._id))
      .map((tenant) => tenant._id);
    console.log("selected Property Data:", selectedPropertyData);

    console.log("Recipients:", recipients);
    setMobileRecipients(recipients);
  };

  // Handle template selection
  const handleTemplateChange = (e) => {
    const type = e.target.value;
    setMessageType(type);
    setEditingTemplate(null);

    // Find the template text
    if (type !== "custom") {
      const template = messageTemplates.find((t) => t.type === type);
      if (template) {
        setMobileMessageText(template.text);
      }
    } else {
      setMobileMessageText("");
    }
  };

  // Start editing a template
  const handleStartEditing = (templateType) => {
    const template = messageTemplates.find((t) => t.type === templateType);
    if (template && template.editable) {
      setEditingTemplate(templateType);
      setEditedTemplateText(template.text);
    }
  };

  // Save edited template
  const handleSaveTemplate = (templateType) => {
    setMessageTemplates(
      messageTemplates.map((template) =>
        template.type === templateType
          ? { ...template, text: editedTemplateText }
          : template
      )
    );

    // If the current message type is the one being edited, update the message text
    if (messageType === templateType) {
      setMobileMessageText(editedTemplateText);
    }

    setEditingTemplate(null);
  };

  // Cancel template editing
  const handleCancelEditing = () => {
    setEditingTemplate(null);
  };

  // Reset message status after 5 seconds
  useEffect(() => {
    if (messageStatus) {
      const timer = setTimeout(() => {
        setMessageStatus(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [messageStatus]);

  // Handle message submission
  const sendMessage = () => {
    handleMobileBroadcast();
    setMessageStatus("Message sent successfully!");
    setSelectedTenants([]);
    setMobileMessageText("");
    setSelectedProperty("");
    setSelectedPropertyData(null);
    setMessageType("custom"); // Reset message type to default
  };

  return (
    <div className="mobile-messages-container">
      <div className="message-wrapper">
        <h2 className="page-title">Mobile SMS Broadcast</h2>

        {isError && <div className="alert error">{errorMessage}</div>}

        {messageStatus && <div className="alert success">{messageStatus}</div>}

        <div className="info-panel">
          <p>
            Send SMS messages to your tenants. Messages will be delivered to
            their registered phone numbers. Each SMS is charged per segment (160
            characters). Choose a template or create your own message.
            {isAutoTargetedMessage && (
              <strong>
                {" "}
                Note: Rent reminders will automatically be sent to tenants with
                pending invoices.
              </strong>
            )}
          </p>
        </div>

        <div className="form-grid">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="property-select">Select Property</label>
              <select
                id="property-select"
                value={selectedProperty}
                onChange={handlePropertyChange}
              >
                <option value="">Select a property</option>
                {properties.map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedProperty && !isAutoTargetedMessage && (
            <div className="form-row">
              <div className="recipients-section">
                <div className="section-header">
                  <h3>Select Recipients</h3>
                  <button
                    className="btn-outline-small"
                    onClick={() => handleSelectAllTenants(selectedPropertyData)}
                  >
                    Select All
                  </button>
                </div>

                <div className="tenants-list">
                  {selectedPropertyData?.tenants?.map((tenant) => (
                    <div className="tenant-checkbox" key={tenant._id}>
                      <input
                        type="checkbox"
                        id={`tenant-${tenant._id}`}
                        checked={selectedTenants.includes(tenant._id)}
                        onChange={() => handleTenantSelection(tenant._id)}
                      />
                      <label htmlFor={`tenant-${tenant._id}`}>
                        {`${tenant.house_no || "Unnamed Tenant"} - ${
                          tenant.phone_number || "No phone"
                        }`}
                      </label>
                    </div>
                  ))}

                  {(!selectedPropertyData?.tenants ||
                    selectedPropertyData.tenants.length === 0) && (
                    <p className="empty-list-message">
                      No tenants found for this property
                    </p>
                  )}
                </div>

                <div className="selected-recipients">
                  <h4>Selected: {selectedTenants.length} recipient(s)</h4>
                  <div className="selected-chips">
                    {selectedPropertyData?.tenants
                      ?.filter((tenant) => selectedTenants.includes(tenant._id))
                      .map((tenant) => (
                        <span className="chip" key={tenant._id}>
                          <span className="chip-label">
                            {tenant.name || tenant.phone_number}
                          </span>
                          <button
                            className="chip-delete"
                            onClick={() => handleTenantSelection(tenant._id)}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedProperty && isAutoTargetedMessage && (
            <div className="form-row">
              <div className="info-panel">
                <p>
                  <strong>Automatic Targeting:</strong> This message will be
                  automatically sent to all tenants with pending invoices in the
                  selected property. No manual selection required.
                </p>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="message-type">Message Template</label>
              <div className="template-selector-container">
                <select
                  id="message-type"
                  value={messageType}
                  onChange={handleTemplateChange}
                  className="template-select"
                >
                  <option value="custom">Custom Message</option>
                  <option value="rent_reminder">Rent Reminder</option>
                  <option value="late_rent_reminder">Late Rent Reminder</option>
                  <option value="maintenance">Maintenance Notice</option>
                  <option value="security">Security Reminder</option>
                  <option value="payment_info">Payment Information</option>
                  <option value="garbage">Garbage Collection</option>
                </select>

                {messageType !== "custom" &&
                  messageTemplates.find((t) => t.type === messageType)
                    ?.editable && (
                    <button
                      className="template-edit-btn"
                      onClick={() => handleStartEditing(messageType)}
                      disabled={editingTemplate !== null}
                      title="Edit template"
                    >
                      <FiEdit2 />
                    </button>
                  )}
              </div>
            </div>

            {editingTemplate === messageType ? (
              <div className="form-group">
                <div className="template-edit-header">
                  <label>Edit Template</label>
                  <div className="template-edit-actions">
                    <button
                      className="template-action-btn save"
                      onClick={() => handleSaveTemplate(messageType)}
                      title="Save changes"
                    >
                      <FiCheck />
                    </button>
                    <button
                      className="template-action-btn cancel"
                      onClick={handleCancelEditing}
                      title="Cancel changes"
                    >
                      <FiX />
                    </button>
                  </div>
                </div>
                <textarea
                  id="template-edit"
                  rows="4"
                  value={editedTemplateText}
                  onChange={(e) => setEditedTemplateText(e.target.value)}
                  placeholder="Edit your template message..."
                  className="template-edit-textarea"
                ></textarea>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="message-content">Message Content</label>
                <textarea
                  id="message-content"
                  rows="4"
                  value={mobileMessageText}
                  onChange={(e) => setMobileMessageText(e.target.value)}
                  disabled={messageType !== "custom" && messageType !== ""}
                  placeholder="Type your message here..."
                ></textarea>
              </div>
            )}

            <div className="message-info">
              <span className="message-count">
                Character count:{" "}
                {
                  (editingTemplate ? editedTemplateText : mobileMessageText)
                    .length
                }
                {(editingTemplate ? editedTemplateText : mobileMessageText)
                  .length > 0 && (
                  <>
                    {" "}
                    (SMS segments:{" "}
                    {Math.ceil(
                      (editingTemplate ? editedTemplateText : mobileMessageText)
                        .length / 160
                    )}
                    )
                  </>
                )}
              </span>

              <span className="message-total">
                Total messages:{" "}
                {selectedTenants.length > 0
                  ? selectedTenants.length *
                    Math.ceil(
                      (editingTemplate ? editedTemplateText : mobileMessageText)
                        .length / 160
                    )
                  : 0}
              </span>
            </div>
          </div>

          <div className="form-row button-row">
            <button
              className={`btn-primary ${
                !selectedProperty ||
                (!isAutoTargetedMessage && selectedTenants.length === 0) ||
                !(
                  editingTemplate ? editedTemplateText : mobileMessageText
                ).trim() ||
                isLoading ||
                editingTemplate !== null
                  ? "disabled"
                  : ""
              }`}
              onClick={sendMessage}
              disabled={
                !selectedProperty ||
                (!isAutoTargetedMessage && selectedTenants.length === 0) ||
                !(
                  editingTemplate ? editedTemplateText : mobileMessageText
                ).trim() ||
                isLoading ||
                editingTemplate !== null
              }
            >
              {isLoading ? <div className="spinner"></div> : "Send Message"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileChatMessages;
